import React from "react";
import { Martini, Church, Monitor, Crown, Swords, Landmark, Icon as LucideLabIcon, type LucideIcon, type LucideProps } from "lucide-react";
import { horseHead } from "@lucide/lab";
import PulpFistIcon from "@/components/ink-insanity/PulpFistIcon";
import era1920s from "@/assets/era-1920s-speakeasy.webp";
import eraGaslight from "@/assets/era-gaslight-victorian.webp";
import eraModern from "@/assets/era-modern-city.webp";
import eraRegency from "@/assets/era-regency-ballroom.webp";
import eraWestern from "@/assets/era-western-frontier.webp";
import eraDarkages from "@/assets/era-darkages-anglosaxon.webp";
import eraInvictus from "@/assets/era-invictus-forum.webp";
import eraPulp from "@/assets/era-pulp-adventure.webp";

const HorseIcon = React.forwardRef<SVGSVGElement, LucideProps>((props, ref) => (
  <LucideLabIcon ref={ref} iconNode={horseHead} {...props} />
));
HorseIcon.displayName = "HorseIcon";

export interface HomepageEra {
  id: "1920s" | "regency" | "gaslight" | "western" | "modern" | "darkages" | "invictus" | "pulp";
  titleKey: string;
  subtitleKey: string;
  descriptionKey: string;
  image: string;
  route: string;
  available: boolean;
  icon: LucideIcon;
  iconColor: string;
  newUntil?: string;
  imagePosition?: string;
}

/**
 * Single source of truth for the homepage era list.
 * Consumed by both the era card grid and the QuickStartFab picker.
 * Adding a new era only requires appending an entry here.
 */
export const HOMEPAGE_ERAS: HomepageEra[] = [
  {
    id: "1920s",
    titleKey: "eras.1920s.title",
    subtitleKey: "eras.1920s.subtitle",
    descriptionKey: "eras.1920s.description",
    image: era1920s,
    route: "/1920s",
    available: true,
    icon: Martini as LucideIcon,
    iconColor: "text-era-1920s",
  },
  {
    id: "invictus",
    titleKey: "eras.invictus.title",
    subtitleKey: "eras.invictus.subtitle",
    descriptionKey: "eras.invictus.description",
    image: eraInvictus,
    route: "/invictus",
    available: true,
    icon: Landmark as LucideIcon,
    iconColor: "text-red-600",
    newUntil: "2026-07-15",
    imagePosition: "object-[center_30%]",
  },
  {
    id: "darkages",
    titleKey: "eras.darkages.title",
    subtitleKey: "eras.darkages.subtitle",
    descriptionKey: "eras.darkages.description",
    image: eraDarkages,
    route: "/dark-ages",
    available: true,
    icon: Swords as LucideIcon,
    iconColor: "text-purple-400",
    newUntil: "2026-06-11",
  },
  {
    id: "regency",
    titleKey: "eras.regency.title",
    subtitleKey: "eras.regency.subtitle",
    descriptionKey: "eras.regency.description",
    image: eraRegency,
    route: "/regency",
    available: true,
    icon: Crown as LucideIcon,
    iconColor: "text-amber-400",
    newUntil: "2026-05-12",
  },
  {
    id: "western",
    titleKey: "eras.western.title",
    subtitleKey: "eras.western.subtitle",
    descriptionKey: "eras.western.description",
    image: eraWestern,
    route: "/western",
    available: true,
    icon: HorseIcon as unknown as LucideIcon,
    iconColor: "text-amber-600",
    newUntil: "2026-06-01",
  },
  {
    id: "gaslight",
    titleKey: "eras.gaslight.title",
    subtitleKey: "eras.gaslight.subtitle",
    descriptionKey: "eras.gaslight.description",
    image: eraGaslight,
    route: "/gaslight",
    available: true,
    icon: Church as LucideIcon,
    iconColor: "text-rose-400",
    newUntil: "2026-05-11",
  },
  {
    id: "pulp",
    titleKey: "eras.pulp.title",
    subtitleKey: "eras.pulp.subtitle",
    descriptionKey: "eras.pulp.description",
    image: eraPulp,
    route: "/pulp-1930s",
    available: true,
    icon: PulpFistIcon as LucideIcon,
    iconColor: "text-gray-300",
    newUntil: "2026-08-01",
  },
  {
    id: "modern",
    titleKey: "eras.modern.title",
    subtitleKey: "eras.modern.subtitle",
    descriptionKey: "eras.modern.description",
    image: eraModern,
    route: "/modern",
    available: true,
    icon: Monitor as LucideIcon,
    iconColor: "text-cyan-400",
    newUntil: "2026-05-12",
  },
];